import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import * as Yup from "yup";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import { InputText } from "../../InputText";
import { Label } from "../../ui/label";
import { Button } from "../../ui/button";
import { Info, Loader2, X } from "lucide-react";
import { api } from "../../../services/api";

interface Status {
  id: string;
  nome_status: string;
  status: "A" | "I";
  ordem_minima_patente: number;
  id_habbo_variavel?: number | null;
  criado_em: string;
  alterado_em: string;
}

interface FormValues {
  nome_status: string;
  status: "A" | "I";
  ordem_minima_patente: number;
  id_habbo_variavel: string;
}

interface StatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingStatus?: Status | null;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  nome_status: Yup.string()
    .required("Nome do status é obrigatório")
    .min(2, "Nome deve ter pelo menos 2 caracteres")
    .max(100, "Nome deve ter no máximo 100 caracteres"),
  status: Yup.string()
    .required("Status é obrigatório")
    .oneOf(["A", "I"], "Status deve ser A (Ativo) ou I (Inativo)"),
  ordem_minima_patente: Yup.number()
    .min(0, "Deve ser maior ou igual a 0")
    .required("Ordem mínima é obrigatória"),
  id_habbo_variavel: Yup.string().matches(/^\d*$/, "Informe um número inteiro maior ou igual a 0"),
});

export function StatusModal({ isOpen, onClose, editingStatus = null, onSuccess }: StatusModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormValues>({
    nome_status: "",
    status: "A",
    ordem_minima_patente: 0,
    id_habbo_variavel: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});

  useEffect(() => {
    if (editingStatus) {
      setFormData({
        nome_status: editingStatus.nome_status,
        status: editingStatus.status,
        ordem_minima_patente: editingStatus.ordem_minima_patente ?? 0,
        id_habbo_variavel:
          editingStatus.id_habbo_variavel !== null && editingStatus.id_habbo_variavel !== undefined
            ? String(editingStatus.id_habbo_variavel)
            : "",
      });
    } else {
      setFormData({
        nome_status: "",
        status: "A",
        ordem_minima_patente: 0,
        id_habbo_variavel: "",
      });
    }
    setErrors({});
  }, [editingStatus, isOpen]);

  const validateForm = () => {
    try {
      validationSchema.validateSync(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (error: any) {
      const validationErrors: any = {};
      error.inner.forEach((err: any) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });
      setErrors(validationErrors);
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        ...formData,
        id_habbo_variavel: formData.id_habbo_variavel !== "" ? parseInt(formData.id_habbo_variavel, 10) : null,
      };

      if (editingStatus) {
        await api.put(`/status/${editingStatus.id}`, payload);
        enqueueSnackbar("Status atualizado com sucesso!", { variant: "success" });
      } else {
        await api.post("/status", payload);
        enqueueSnackbar("Status criado com sucesso!", { variant: "success" });
      }

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || "Erro ao salvar status", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ nome_status: "", status: "A", ordem_minima_patente: 0, id_habbo_variavel: "" });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
              {editingStatus ? "Editar Status" : "Novo Status"}
            </CardTitle>
            <CardDescription>
              {editingStatus
                ? "Atualize as informações do status"
                : "Crie um novo status do sistema"}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="h-6 w-6 p-0"
            disabled={loading}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome_status">Nome do Status</Label>
              <InputText
                id="nome_status"
                name="nome_status"
                title=""
                value={formData.nome_status}
                onChange={(e) => setFormData({ ...formData, nome_status: e.target.value })}
                placeholder="Ex: Ativo, Inativo, Licença..."
                error={errors.nome_status}
                className="mt-1"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as "A" | "I" })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={loading}
              >
                <option value="A">A - Ativo</option>
                <option value="I">I - Inativo</option>
              </select>
              {errors.status && (
                <p className="text-sm text-destructive mt-1">{errors.status}</p>
              )}
            </div>

            <div>
              <Label htmlFor="ordem_minima_patente">Ordem mínima da patente</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Patentes com ordem abaixo deste valor não poderão atribuir este status. Use 0 para liberar a todos.
              </p>
              <input
                id="ordem_minima_patente"
                type="number"
                min={0}
                value={formData.ordem_minima_patente}
                onChange={(e) => setFormData({ ...formData, ordem_minima_patente: Number(e.target.value) })}
                className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                disabled={loading}
              />
              {errors.ordem_minima_patente && (
                <p className="text-sm text-destructive mt-1">{errors.ordem_minima_patente}</p>
              )}
            </div>

            <div>
              <Label htmlFor="id_habbo_variavel">ID da Variável no Habbo</Label>
              <InputText
                id="id_habbo_variavel"
                name="id_habbo_variavel"
                title=""
                type="number"
                min={0}
                value={formData.id_habbo_variavel}
                onChange={(e) => setFormData({ ...formData, id_habbo_variavel: e.target.value })}
                placeholder="Ex: 1, 2, 3... (opcional)"
                error={errors.id_habbo_variavel}
                className="mt-1"
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Valor que representa este status na variável do jogo. Deixe em branco se não houver.
              </p>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {editingStatus ? "Atualizando..." : "Criando..."}
                  </>
                ) : (
                  editingStatus ? "Atualizar" : "Criar"
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
